// Cálculos puros de "Gestión HH": portados 1:1 desde el prototipo React.
// Sin dependencias de framework. La matemática es idéntica al original para
// que los valores demo (deterministas vía pseudo01) se reproduzcan igual.
import { stageTotals } from "./gestionHHMock.js";

/**
 * Pseudoaleatorio determinista 0-1 a partir de una semilla (demo de semanas
 * pasadas: los valores son estables entre renders y recargas).
 * @param {string} seed
 * @returns {number}
 */
export function pseudo01(seed) {
  let h = 7;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 9973;
  return h / 9973;
}

/**
 * Etiquetas de mes ("jul-26", "ago-26"…) desde el mes de inicio.
 * El prototipo localizaba el mes corto; este port fija el locale español.
 * @param {string} mesInicio  formato "YYYY-MM"
 * @param {number} numMeses
 * @returns {string[]}
 */
export function monthLabels(mesInicio, numMeses) {
  const [y0, m0] = String(mesInicio).split("-").map(Number);
  const len = Math.max(1, numMeses);
  const out = [];
  for (let i = 0; i < len; i++) {
    const d = new Date(y0 || 2026, (m0 || 1) - 1 + i, 1);
    const mes = d.toLocaleDateString("es", { month: "short" }).replace(".", "");
    out.push(`${mes}-${String(d.getFullYear() % 100).padStart(2, "0")}`);
  }
  return out;
}

/**
 * QNT horas disponible por persona y semana:
 * horas semana × (1 − % folga) × (1 + % extras) × (1 − % feriado).
 * @param {{horasSemana:number,perdaFolga:number,horasExtras:number,perdaFeriado:number}} premisas
 * @returns {number}
 */
export function qntHorasDisponivel(premisas) {
  const p = premisas || {};
  const horasSemana = p.horasSemana || 0;
  const perdaFolga = p.perdaFolga || 0;
  const horasExtras = p.horasExtras || 0;
  const perdaFeriado = p.perdaFeriado || 0;
  const value = horasSemana * (1 - perdaFolga / 100) * (1 + horasExtras / 100) * (1 - perdaFeriado / 100);
  return Math.round(value * 1000) / 1000;
}

/**
 * Versión pura de autoDistribuir(): reparte el % restante de cada etapa con
 * saldo pendiente entre los meses ≥ mes actual que están a 0 (o todos ≥ actual
 * si ninguno está a 0). Suma exacta 100 por mayor resto, a 2 decimales.
 * Respeta los valores manuales existentes. Devuelve un NUEVO objeto pct.
 *
 * @param {Array} disciplinas  dataset getDisciplinas()
 * @param {Object} pct         overrides actuales { rowKey: { mi: number } }
 * @param {number} mesActualIdx índice del mes actual (acotado por el llamador)
 * @param {number} numMeses
 * @returns {Object} nuevo objeto pct
 */
export function autoDistribuirPct(disciplinas, pct, mesActualIdx, numMeses) {
  const len = Math.max(1, numMeses);
  const todayIdx = Math.max(0, Math.min(len - 1, mesActualIdx));
  const next = { ...(pct || {}) };
  for (const d of disciplinas) {
    for (const st of d.stages) {
      const stt = stageTotals(st);
      if (stt.saldo <= 0) continue;
      const rowKey = `${d.disciplina}||${st.key}`;
      const cur = next[rowKey] || {};
      // Σ % ya asignado en todo el horizonte.
      let asignado = 0;
      for (let mi = 0; mi < len; mi++) asignado += cur[mi] || 0;
      const rem = Math.round((100 - asignado) * 100) / 100;
      if (rem <= 0) continue;
      // Meses candidatos: desde el actual hasta el final.
      const desde = [];
      for (let mi = 0; mi < len; mi++) if (mi >= todayIdx) desde.push(mi);
      let target = desde.filter((mi) => (cur[mi] || 0) === 0);
      if (target.length === 0) target = desde;
      if (target.length === 0) continue;
      // Reparto equitativo con suma exacta (mayor resto, 2 decimales).
      const centesimas = Math.round(rem * 100);
      const base = Math.floor(centesimas / target.length);
      let sobra = centesimas - base * target.length;
      const fila = { ...cur };
      for (const mi of target) {
        const extra = sobra > 0 ? 1 : 0;
        if (sobra > 0) sobra--;
        fila[mi] = Math.round((cur[mi] || 0) * 100 + base + extra) / 100;
      }
      next[rowKey] = fila;
    }
  }
  return next;
}

/**
 * Cálculos de la plantilla semanal, compartidos por la pestaña y el export.
 * Portado 1:1 del prototipo React.
 * @param {{mesInicio:string, monthLabelsLen:number, semanaActual:number,
 *          pctFor:(rowKey:string,mi:number)=>number,
 *          vals:Object, fatorK:Object, hhSemana:number}} cfg
 * @returns {{weeksIn,todayIdx,monthWeekNums,monthIdxOfWeek,kFor,planFor,realFor,valsFor,hhRow}}
 */
export function makeSemanalCalc(cfg) {
  const { mesInicio, monthLabelsLen, semanaActual, pctFor, vals, fatorK, hhSemana } = cfg;

  // Nº de semanas de un mes del horizonte (días del mes ÷ 7, redondeado).
  const weeksIn = (mi) => {
    const [y0, m0] = String(mesInicio).split("-").map(Number);
    const d = new Date(y0 || 2026, (m0 || 1) - 1 + mi, 1);
    const days = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return Math.max(1, Math.round(days / 7));
  };

  // Índice del mes de hoy dentro del horizonte, acotado.
  const todayIdx = (() => {
    const [y0, m0] = String(mesInicio).split("-").map(Number);
    const now = new Date();
    return Math.max(
      0,
      Math.min(monthLabelsLen - 1, (now.getFullYear() - (y0 || now.getFullYear())) * 12 + (now.getMonth() + 1 - (m0 || 1))),
    );
  })();

  // Números de semana que caen dentro del mes `mi`, anclando la semana actual
  // al mes de hoy (según su semana dentro del mes).
  const monthWeekNums = (mi) => {
    const now = new Date();
    // Semana de la actual dentro del mes de hoy (1-based).
    const wom = Math.min(weeksIn(todayIdx), Math.ceil(now.getDate() / 7));
    // Desplazamiento en semanas entre el inicio del mes `mi` y el de hoy.
    let offset = 0;
    for (let i = Math.min(mi, todayIdx); i < Math.max(mi, todayIdx); i++) offset += weeksIn(i);
    if (mi > todayIdx) offset = -offset;
    const first = semanaActual - (wom - 1) - offset;
    return Array.from({ length: weeksIn(mi) }, (_, i) => first + i);
  };

  // Mes del horizonte al que pertenece una semana, o null si queda fuera.
  const monthIdxOfWeek = (w) => {
    let start = monthWeekNums(0)[0];
    for (let mi = 0; mi < monthLabelsLen; mi++) {
      const n = weeksIn(mi);
      if (w >= start && w < start + n) return mi;
      start += n;
    }
    return null;
  };

  const kFor = (rowKey) => (fatorK[rowKey] != null ? fatorK[rowKey] : 1.1);

  // FTD planificada (calculada) de una fila en una semana.
  const planFor = (rowKey, stt, week) => {
    const mi = monthIdxOfWeek(week);
    if (mi == null || stt.saldo <= 0) return 0;
    const pct = pctFor(rowKey, mi);
    if (pct <= 0) return 0;
    return Math.round(((stt.saldo * pct) / 100 / weeksIn(mi)) * 100) / 100;
  };

  // FTD real: reparto determinista del ejecutado del takeoff (demo).
  const realFor = (week, rowKey, stt) => {
    // Las semanas futuras aún no tienen avances registrados en el takeoff.
    if (week > semanaActual || stt.ejecutado <= 0) return 0;
    const p3 = pseudo01(`${week}|${rowKey}|real`);
    const plan = planFor(rowKey, stt, week);
    const base = stt.saldo > 0 ? stt.saldo : stt.cantidad;
    const ref = plan > 0 ? plan : base > 0 ? base / 14 : stt.ejecutado / 26;
    return Math.round(ref * (0.55 + 0.55 * p3) * 100) / 100;
  };

  // Valores de una etapa en una semana. FTD planificada y real las pone el
  // sistema; efectivo/programado editables (≥ actual) o demo (< actual).
  const valsFor = (week, rowKey, stt) => {
    const plan = planFor(rowKey, stt, week);
    const real = realFor(week, rowKey, stt);
    if (week >= semanaActual) {
      const v = vals[`${week}||${rowKey}`] || {};
      return { ef: v.ef != null ? v.ef : 0, plan, prog: v.prog != null ? v.prog : 0, real };
    }
    const base = stt.saldo > 0 ? stt.saldo : stt.cantidad;
    if (base <= 0) return { ef: 0, plan, prog: 0, real };
    const p2 = pseudo01(`${week}|${rowKey}|prog`);
    const p4 = pseudo01(`${week}|${rowKey}|ef`);
    const ref = plan > 0 ? plan : base / 14;
    const prog = Math.round(ref * (0.75 + 0.5 * p2) * 100) / 100;
    const ef = Math.round(3 + p4 * 25);
    return { ef, plan, prog, real };
  };

  // HH reales: demo determinista alrededor de índice ajustado × FTD real.
  const hhRealFor = (week, rowKey, stt, wv) => {
    const ind = stt.indiceHH != null ? stt.indiceHH * kFor(rowKey) : null;
    if (ind == null) return null;
    // Las semanas futuras aún no tienen HH registradas en el takeoff.
    if (week > semanaActual || wv.real <= 0) return 0;
    const p = pseudo01(`${week}|${rowKey}|hhreal`);
    return Math.round(ind * wv.real * (0.85 + 0.3 * p) * 100) / 100;
  };

  // HH derivadas de una etapa en una semana. null sin índice ajustable.
  const hhRow = (stt, rowKey, wv, week) => {
    const ind = stt.indiceHH != null ? stt.indiceHH * kFor(rowKey) : null;
    return {
      hhPlan: ind != null ? ind * wv.plan : null,
      hhDisp: wv.ef * hhSemana,
      hhProg: ind != null ? ind * wv.prog : null,
      hhReal: hhRealFor(week, rowKey, stt, wv),
    };
  };

  return { weeksIn, todayIdx, monthWeekNums, monthIdxOfWeek, kFor, planFor, realFor, valsFor, hhRow };
}

/**
 * HH agregadas de una disciplina en una semana (Dashboard HH, demo determinista).
 * Requiere stageTotals (importado de ./gestionHHMock).
 * @param {{disciplina:string, stages:Array}} d
 * @param {number} w  número de semana
 * @param {number} semanaActual
 * @param {number} qnt  QNT horas disponible
 * @returns {{plan:number, disp:number, prog:number, real:number|null}}
 */
export function dashHHWeek(d, w, semanaActual, qnt) {
  let plan = 0;
  let disp = 0;
  let prog = 0;
  let real = w <= semanaActual ? 0 : null;
  for (const st of d.stages) {
    const stt = stageTotals(st);
    const rowKey = `${d.disciplina}||${st.key}`;
    const ind = stt.indiceHH != null ? stt.indiceHH * 1.1 : null;
    const base = stt.saldo > 0 ? stt.saldo : stt.cantidad;
    if (ind == null || base <= 0) continue;
    const ftdPlan = base / 14;
    plan += ind * ftdPlan;
    disp += Math.round(3 + pseudo01(`${w}|${rowKey}|ef`) * 25) * qnt;
    prog += ind * ftdPlan * (0.75 + 0.5 * pseudo01(`${w}|${rowKey}|prog`));
    if (real != null && stt.ejecutado > 0) {
      real += ind * ftdPlan * (0.75 + 0.55 * pseudo01(`${w}|${rowKey}|real`));
    }
  }
  return { plan, disp, prog, real };
}

/**
 * Ratio a % entero, o null si no es computable.
 * @param {number|null} a
 * @param {number|null} b
 * @returns {number|null}
 */
export function ratioPct(a, b) {
  return a == null || b == null || b <= 0 ? null : Math.round((a / b) * 100);
}
